/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import './App.css';

// Configuration for BSC Mainnet
const CONFIG = {
    TREASUREVAULT_CONTRACT: "0xE19285379A3969A3599Ba46C3D07F880b7CA0CD2", // Update with your deployed Smartcore contract address
    USDT_CONTRACT: "0x9e5aac1ba1a2e6aed6b32689dfcf62a509ca96f3", // BSC Mainnet USDT
    CHAIN_ID: 204, // BSC Mainnet
    MAIN_RPC: "https://opbnb-mainnet-rpc.bnbchain.org",
    BACKUP_RPC: "https://opbnb-mainnet-rpc.bnbchain.org"
};

// Updated ABI matching the Smartcore contract
const TREASUREVAULT_ABI = [
    // Main User Functions
    "function register(uint256 referrerId) external",
    "function upgradePackage(uint256 packageId) external", 
    "function retopupPackage(uint256 packageId) external",
    "function claimIncome() external",
    "function claimPoolIncome(uint256 packageId) external",
    "function claimRoyaltyIncome() external", 
    "function claimAllIncome() external",
    
    // Admin Functions
    "function setPoolIncomePerUser(uint256 packageId, uint256 amountPerUser) external",
    "function setRoyaltyIncomePerUser(uint256 amountPerUser) external",
    "function claimFullReservePool() external",
    
    // Pool accumulation functions
    "function getPoolData() external view returns (uint256 poolAccumulated, uint256 royaltyAccumulated, uint256[7] memory poolEligibleCounts, uint256 royaltyEligibleCount)",
    "function getPoolAccumulationBreakdown() external view returns (uint256 totalAccumulation, uint256[7] memory packageAccumulations)",
    "function getPackagePoolAccumulation(uint256 packageId) external view returns (uint256)",
    "function resetPoolAccumulation() external",
    "function resetPackagePoolAccumulation(uint256 packageId) external",
    "function resetAllPackagePoolAccumulations() external",
    "function resetRoyaltyAccumulation() external",
    
    // View Functions
    "function getUserDetails(uint256 userId) external view returns (address account, uint256 id, uint256 referrer, bool isActive, uint256 totalDirectReferrals, uint256 claimableAmount, uint256 totalClaimed, uint256 totalIncome)",
    "function getUserPackageDetails(uint256 userId, uint256 packageId) external view returns (bool isActive, uint256 activationTime, uint256 totalInvested, uint256 totalEarned, uint256 ceilingLimit, uint256 remainingLimit)",
    "function getUserPackages(uint256 userId) external view returns (bool[8] memory)",
    "function getUserReferrals(uint256 userId) external view returns (uint256[] memory)",
    "function getUserIncomeBreakdown(uint256 userId) external view returns (uint256 directReferralIncome, uint256 matrixLevelIncome, uint256 normalLevelIncome, uint256 poolIncome, uint256 royaltyIncome, uint256 totalIncome, uint256 claimableAmount, uint256 totalClaimed)",
    
    // Pull-based system view functions
    "function getUserClaimablePoolIncome(uint256 userId, uint256 packageId) external view returns (uint256)",
    "function getUserClaimableRoyaltyIncome(uint256 userId) external view returns (uint256)",
    "function getUserTotalClaimableIncome(uint256 userId) external view returns (uint256 regularIncome, uint256 poolIncome, uint256 royaltyIncome, uint256 totalClaimable)",
    "function getPoolDistributionInfo(uint256 packageId) external view returns (uint256 currentRound, uint256 latestAmountPerUser, uint256 totalRounds)",
    "function getRoyaltyDistributionInfo() external view returns (uint256 currentRound, uint256 latestAmountPerUser, uint256 totalRounds)",
    
    "function getMatrixDetails(uint256 userId, uint256 packageId) external view returns (uint256 upline, uint256[] memory directDownlines, uint256 totalTeamSize)",
    "function getMatrixLevelUsers(uint256 userId, uint256 packageId, uint256 level) external view returns (uint256[] memory)",
    "function getMatrixLevelCounts(uint256 userId, uint256 packageId) external view returns (uint256[10] memory)",
    "function getNormalLevelCounts(uint256 userId, uint256 packageId) external view returns (uint256[10] memory)",
    "function getNormalLevelUsers(uint256 userId, uint256 packageId, uint256 level) external view returns (uint256[] memory)",
    "function getLevelCompletionStatus(uint256 userId, uint256 packageId) external view returns (bool[10] memory)",
    
    // Public Variables
    "function addressToId(address) external view returns (uint256)",
    "function totalUsers() external view returns (uint256)",
    "function lastUserId() external view returns (uint256)",
    "function packagePrices(uint256) external view returns (uint256)",
    "function reservePool() external view returns (uint256)",
    "function defaultReferrer() external view returns (uint256)",
    "function poolEligibility(uint256, uint256) external view returns (bool)",
    "function royaltyEligibility(uint256) external view returns (bool)",
    "function currentPoolRound(uint256) external view returns (uint256)",
    "function currentRoyaltyRound() external view returns (uint256)",
    "function poolFundsAccumulated() external view returns (uint256)",
    "function poolFundsAccumulatedByPackage(uint256) external view returns (uint256)",
    "function royaltyFundsAccumulated() external view returns (uint256)",
    "function poolEligibleUserCount(uint256) external view returns (uint256)",
    "function royaltyEligibleUserCount() external view returns (uint256)",
    
    // Contract info
    "function owner() external view returns (address)",
    "function admin() external view returns (address)",
    "function routerWallet() external view returns (address)",
    "function teamWallet1() external view returns (address)",
    "function teamWallet2() external view returns (address)",
    "function updateAdmin(address _admin) external",
    "function updateWallets(address _routerWallet, address _teamWallet1, address _teamWallet2) external",
    "function transferOwnership(address newOwner) external"
];

const USDT_ABI = [
    "function balanceOf(address account) external view returns (uint256)",
    "function approve(address spender, uint256 amount) external returns (bool)",
    "function allowance(address owner, address spender) external view returns (uint256)",
    "function transfer(address to, uint256 amount) external returns (bool)",
    "function decimals() external view returns (uint8)"
];

const SmartcoreAdminPanel = () => {
    // State management
    const [provider, setProvider] = useState(null);
    const [backupProvider, setBackupProvider] = useState(null);
    const [signer, setSigner] = useState(null);
    const [treasurevaultContract, setTreasurevaultContract] = useState(null);
    const [readOnlyContract, setReadOnlyContract] = useState(null);
    const [usdtContract, setUsdtContract] = useState(null);
    const [currentAccount, setCurrentAccount] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(0);
    const [connected, setConnected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [ownerAddress, setOwnerAddress] = useState(null);
    const [adminAddress, setAdminAddress] = useState(null);
    
    // Contract data state
    const [totalUsers, setTotalUsers] = useState('0');
    const [lastUserId, setLastUserId] = useState('0');
    const [reservePool, setReservePool] = useState('0');
    const [poolDistributions, setPoolDistributions] = useState({});
    const [royaltyDistribution, setRoyaltyDistribution] = useState({ currentRound: '0', latestAmount: '0', totalRounds: '0' });
    const [usdtBalance, setUsdtBalance] = useState('0');
    const [userDetails, setUserDetails] = useState(null);
    const [userClaimableAmounts, setUserClaimableAmounts] = useState({ 
        regular: '0', 
        pool: '0', 
        royalty: '0', 
        total: '0' 
    });
    
    // Pool accumulation and eligibility counter states
    const [poolFundsAccumulated, setPoolFundsAccumulated] = useState('0');
    const [royaltyFundsAccumulated, setRoyaltyFundsAccumulated] = useState('0');
    const [poolEligibleCounts, setPoolEligibleCounts] = useState({});
    const [royaltyEligibleCount, setRoyaltyEligibleCount] = useState('0');
    const [packagePoolAccumulations, setPackagePoolAccumulations] = useState({});
    
    // User lookup states
    const [searchAddress, setSearchAddress] = useState('');
    const [searchUserId, setSearchUserId] = useState('');
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    
    // Form states
    const [eligibilityCheckForm, setEligibilityCheckForm] = useState({ userId: '', packageId: '' });
    const [poolDistForm, setPoolDistForm] = useState({ packageId: '', amount: '' });
    const [royaltyDistForm, setRoyaltyDistForm] = useState({ amount: '' });
    const [userSearchForm, setUserSearchForm] = useState({ userId: '' });
    
    // Pool calculation states
    const [poolCalculation, setPoolCalculation] = useState({ totalRequired: '0', eligibleCount: 0 });
    const [royaltyCalculation, setRoyaltyCalculation] = useState({ totalRequired: '0', eligibleCount: 0 });
    
    // Modal states
    const [showEligibleUsersModal, setShowEligibleUsersModal] = useState(false);
    const [selectedPoolPackage, setSelectedPoolPackage] = useState(null);
    const [eligibleUsers, setEligibleUsers] = useState([]);
    const [loadingEligibleUsers, setLoadingEligibleUsers] = useState(false);

    // Initialize providers and contracts
    useEffect(() => {
        initializeProviders();
    }, []);

    const initializeProviders = async () => {
        try {
            // Initialize read-only providers (ethers v5)
            const mainProvider = new ethers.providers.JsonRpcProvider(CONFIG.MAIN_RPC);
            const backupProv = new ethers.providers.JsonRpcProvider(CONFIG.BACKUP_RPC);
            
            setProvider(mainProvider);
            setBackupProvider(backupProv);
            
            // Initialize read-only contracts
            const readContract = new ethers.Contract(
                CONFIG.TREASUREVAULT_CONTRACT,
                TREASUREVAULT_ABI,
                mainProvider
            );
            setReadOnlyContract(readContract);
            
            // Load contract authorization addresses
            await loadAuthorizationAddresses(readContract);
            
            // Load initial data
            await loadContractStatistics(readContract);
            await loadPoolDistributions(readContract);
            await loadRoyaltyDistribution(readContract);
            await loadPoolData(readContract);
            
        } catch (error) {
            console.error('Error initializing providers:', error);
            showMessage('Failed to initialize providers. Check console for details.', 'error');
        }
    };

    const loadAuthorizationAddresses = async (contract) => {
        try {
            const [owner, admin] = await Promise.all([
                contract.owner(),
                contract.admin()
            ]);
            setOwnerAddress(owner.toLowerCase());
            setAdminAddress(admin.toLowerCase());
        } catch (error) {
            console.error('Error loading authorization addresses:', error);
        }
    };

    const checkAuthorization = (address) => {
        if (!address || !ownerAddress || !adminAddress) return false;
        const addressLower = address.toLowerCase();
        return addressLower === ownerAddress || addressLower === adminAddress;
    };

    const connectWallet = async () => {
        try {
            if (!window.ethereum) {
                showMessage('MetaMask not detected. Please install MetaMask.', 'error');
                return;
            }

            setLoading(true);
            
            // Request account access
            const accounts = await window.ethereum.request({ 
                method: 'eth_requestAccounts' 
            });
            
            // Check network
            const chainId = await window.ethereum.request({ 
                method: 'eth_chainId' 
            });
            
            if (parseInt(chainId, 16) !== CONFIG.CHAIN_ID) {
                try {
                    await window.ethereum.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: `0x${CONFIG.CHAIN_ID.toString(16)}` }],
                    });
                } catch (switchError) {
                    if (switchError.code === 4902) {
                        showMessage('Please add BSC Mainnet to MetaMask', 'error');
                    }
                    throw switchError;
                }
            }

            // Create provider and signer (ethers v5)
            const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
            const web3Signer = web3Provider.getSigner(); // Synchronous in v5
            
            setSigner(web3Signer);
            setCurrentAccount(accounts[0]);
            setConnected(true);
            
            // Check authorization
            const authorized = checkAuthorization(accounts[0]);
            setIsAuthorized(authorized);
            
            if (!authorized) {
                showMessage('⚠️ Access Denied: This wallet is not authorized. Only owner or admin can access this panel.', 'error');
                // Still allow connection to show the access denied message
            } else {
                showMessage('✓ Access granted. Welcome, admin!', 'success');
            }
            
            // Initialize contracts with signer
            const contract = new ethers.Contract(
                CONFIG.TREASUREVAULT_CONTRACT,
                TREASUREVAULT_ABI,
                web3Signer
            );
            setTreasurevaultContract(contract);
            
            const usdtContractInstance = new ethers.Contract(
                CONFIG.USDT_CONTRACT,
                USDT_ABI,
                web3Signer
            );
            setUsdtContract(usdtContractInstance);
            
            // Load user data
            await loadUserData(accounts[0], readOnlyContract);
            await loadUSDTBalance(accounts[0], usdtContractInstance);
            
            showMessage('Wallet connected successfully!', 'success');
            
        } catch (error) {
            console.error('Error connecting wallet:', error);
            showMessage('Failed to connect wallet: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const disconnectWallet = () => {
        setConnected(false);
        setCurrentAccount(null);
        setSigner(null);
        setTreasurevaultContract(null);
        setUsdtContract(null);
        setCurrentUserId(0);
        setUserDetails(null);
        setIsAuthorized(false);
        showMessage('Wallet disconnected', 'info');
    };

    // Load contract statistics
    const loadContractStatistics = async (contract) => {
        try {
            const [total, last, reserve] = await Promise.all([
                contract.totalUsers(),
                contract.lastUserId(),
                contract.reservePool()
            ]);
            
            setTotalUsers(total.toString());
            setLastUserId(last.toString());
            setReservePool(ethers.utils.formatEther(reserve));
        } catch (error) {
            console.error('Error loading contract statistics:', error);
        }
    };

    // Load pool data including per-package accumulation
    const loadPoolData = async (contract) => {
        try {
            const poolData = await contract.getPoolData();
            const breakdown = await contract.getPoolAccumulationBreakdown();
            
            setPoolFundsAccumulated(ethers.utils.formatEther(poolData.poolAccumulated));
            setRoyaltyFundsAccumulated(ethers.utils.formatEther(poolData.royaltyAccumulated));
            setRoyaltyEligibleCount(poolData.royaltyEligibleCount.toString());
            
            // Set pool eligible counts for packages 2-8
            const counts = {};
            for (let i = 0; i < 7; i++) {
                counts[i + 2] = poolData.poolEligibleCounts[i].toString();
            }
            setPoolEligibleCounts(counts);
            
            // Set per-package accumulations
            const accumulations = {};
            for (let i = 0; i < 7; i++) {
                accumulations[i + 2] = ethers.utils.formatEther(breakdown.packageAccumulations[i]);
            }
            setPackagePoolAccumulations(accumulations);
            
        } catch (error) {
            console.error('Error loading pool data:', error);
        }
    };

    // Load pool distributions
    const loadPoolDistributions = async (contract) => {
        try {
            const distributions = {};
            for (let i = 2; i <= 8; i++) {
                const info = await contract.getPoolDistributionInfo(i);
                distributions[i] = {
                    currentRound: info.currentRound.toString(),
                    latestAmount: ethers.utils.formatEther(info.latestAmountPerUser),
                    totalRounds: info.totalRounds.toString()
                };
            }
            setPoolDistributions(distributions);
        } catch (error) {
            console.error('Error loading pool distributions:', error);
        }
    };

    // Load royalty distribution
    const loadRoyaltyDistribution = async (contract) => {
        try {
            const info = await contract.getRoyaltyDistributionInfo();
            setRoyaltyDistribution({
                currentRound: info.currentRound.toString(),
                latestAmount: ethers.utils.formatEther(info.latestAmountPerUser),
                totalRounds: info.totalRounds.toString()
            });
        } catch (error) {
            console.error('Error loading royalty distribution:', error);
        }
    };

    // Load user data
    const loadUserData = async (address, contract) => {
        try {
            const userId = await contract.addressToId(address);
            setCurrentUserId(userId.toString());
            
            if (userId.toString() !== '0') {
                const details = await contract.getUserDetails(userId);
                const breakdown = await contract.getUserIncomeBreakdown(userId);
                const claimable = await contract.getUserTotalClaimableIncome(userId);
                
                setUserDetails({
                    account: details.account,
                    id: details.id.toString(),
                    referrer: details.referrer.toString(),
                    isActive: details.isActive,
                    totalDirectReferrals: details.totalDirectReferrals.toString(),
                    claimableAmount: ethers.utils.formatEther(details.claimableAmount),
                    totalClaimed: ethers.utils.formatEther(details.totalClaimed),
                    totalIncome: ethers.utils.formatEther(details.totalIncome),
                    breakdown: {
                        directReferral: ethers.utils.formatEther(breakdown.directReferralIncome),
                        matrixLevel: ethers.utils.formatEther(breakdown.matrixLevelIncome),
                        normalLevel: ethers.utils.formatEther(breakdown.normalLevelIncome),
                        pool: ethers.utils.formatEther(breakdown.poolIncome),
                        royalty: ethers.utils.formatEther(breakdown.royaltyIncome)
                    }
                });
                
                setUserClaimableAmounts({
                    regular: ethers.utils.formatEther(claimable.regularIncome),
                    pool: ethers.utils.formatEther(claimable.poolIncome),
                    royalty: ethers.utils.formatEther(claimable.royaltyIncome),
                    total: ethers.utils.formatEther(claimable.totalClaimable)
                });
            }
        } catch (error) {
            console.error('Error loading user data:', error);
        }
    };

    // Load USDT balance
    const loadUSDTBalance = async (address, usdtContract) => {
        try {
            const balance = await usdtContract.balanceOf(address);
            setUsdtBalance(ethers.utils.formatEther(balance));
        } catch (error) {
            console.error('Error loading USDT balance:', error);
        }
    };

    // Calculate pool distribution total
    const calculatePoolDistribution = (packageId, amountPerUser) => {
        if (!packageId || !amountPerUser || parseFloat(amountPerUser) <= 0) {
            setPoolCalculation({ totalRequired: '0', eligibleCount: 0 });
            return;
        }
        
        const pid = parseInt(packageId);
        if (pid < 2 || pid > 8) {
            setPoolCalculation({ totalRequired: '0', eligibleCount: 0 });
            return;
        }
        
        const eligibleCount = parseInt(poolEligibleCounts[pid] || 0);
        const totalRequired = (parseFloat(amountPerUser) * eligibleCount).toFixed(2);
        
        setPoolCalculation({ totalRequired, eligibleCount });
    };

    // Calculate royalty distribution total
    const calculateRoyaltyDistribution = (amountPerUser) => {
        if (!amountPerUser || parseFloat(amountPerUser) <= 0) {
            setRoyaltyCalculation({ totalRequired: '0', eligibleCount: 0 });
            return;
        }
        
        const eligibleCount = parseInt(royaltyEligibleCount || 0);
        const totalRequired = (parseFloat(amountPerUser) * eligibleCount).toFixed(2);
        
        setRoyaltyCalculation({ totalRequired, eligibleCount });
    };

    // Update pool calculation when form changes
    useEffect(() => {
        calculatePoolDistribution(poolDistForm.packageId, poolDistForm.amount);
    }, [poolDistForm.packageId, poolDistForm.amount, poolEligibleCounts]);

    // Update royalty calculation when form changes
    useEffect(() => {
        calculateRoyaltyDistribution(royaltyDistForm.amount);
    }, [royaltyDistForm.amount, royaltyEligibleCount]);

    // Set pool income per user
    const setPoolIncome = async () => {
        if (!treasurevaultContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!poolDistForm.packageId || !poolDistForm.amount) {
            showMessage('Please enter package ID and amount', 'error');
            return;
        }
        
        const packageId = parseInt(poolDistForm.packageId);
        if (packageId < 2 || packageId > 8) {
            showMessage('Package ID must be between 2 and 8', 'error');
            return;
        }
        
        try {
            setLoading(true);
            showMessage('Setting pool income...', 'info');
            
            const amountInWei = ethers.utils.parseEther(poolDistForm.amount);
            const tx = await treasurevaultContract.setPoolIncomePerUser(packageId, amountInWei);
            
            showMessage('Transaction submitted. Waiting for confirmation...', 'info');
            await tx.wait();
            
            showMessage(`Pool income set for Package ${packageId}!`, 'success');
            setPoolDistForm({ packageId: '', amount: '' });
            
            // Refresh data
            await loadPoolDistributions(readOnlyContract);
            
        } catch (error) {
            console.error('Error setting pool income:', error);
            showMessage('Failed to set pool income: ' + (error.reason || error.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Set royalty income per user
    const setRoyaltyIncome = async () => {
        if (!treasurevaultContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!royaltyDistForm.amount) {
            showMessage('Please enter amount', 'error');
            return;
        }
        
        try {
            setLoading(true);
            showMessage('Setting royalty income...', 'info');
            
            const amountInWei = ethers.utils.parseEther(royaltyDistForm.amount);
            const tx = await treasurevaultContract.setRoyaltyIncomePerUser(amountInWei);
            
            showMessage('Transaction submitted. Waiting for confirmation...', 'info');
            await tx.wait();
            
            showMessage('Royalty income set successfully!', 'success');
            setRoyaltyDistForm({ amount: '' });
            
            // Refresh data
            await loadRoyaltyDistribution(readOnlyContract);
            
        } catch (error) {
            console.error('Error setting royalty income:', error);
            showMessage('Failed to set royalty income: ' + (error.reason || error.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Reset pool accumulation
    const resetPoolAccumulation = async () => {
        if (!treasurevaultContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!window.confirm('Are you sure you want to reset the total pool accumulation?')) {
            return;
        }
        
        try {
            setLoading(true);
            showMessage('Resetting pool accumulation...', 'info');
            
            const tx = await treasurevaultContract.resetPoolAccumulation();
            
            showMessage('Transaction submitted. Waiting for confirmation...', 'info');
            await tx.wait();
            
            showMessage('Pool accumulation reset successfully!', 'success');
            
            // Refresh data
            await loadPoolData(readOnlyContract);
            
        } catch (error) {
            console.error('Error resetting pool accumulation:', error);
            showMessage('Failed to reset pool accumulation: ' + (error.reason || error.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Reset package pool accumulation
    const resetPackageAccumulation = async (packageId) => {
        if (!treasurevaultContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!window.confirm(`Are you sure you want to reset Package ${packageId} pool accumulation?`)) {
            return;
        }
        
        try {
            setLoading(true);
            showMessage(`Resetting Package ${packageId} accumulation...`, 'info');
            
            const tx = await treasurevaultContract.resetPackagePoolAccumulation(packageId);
            
            showMessage('Transaction submitted. Waiting for confirmation...', 'info');
            await tx.wait();
            
            showMessage(`Package ${packageId} accumulation reset successfully!`, 'success');
            
            // Refresh data
            await loadPoolData(readOnlyContract);
            
        } catch (error) {
            console.error('Error resetting package accumulation:', error);
            showMessage('Failed to reset package accumulation: ' + (error.reason || error.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Reset all package pool accumulations
    const resetAllPackageAccumulations = async () => {
        if (!treasurevaultContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!window.confirm('Are you sure you want to reset ALL package pool accumulations?')) {
            return;
        }
        
        try {
            setLoading(true);
            showMessage('Resetting all package accumulations...', 'info');
            
            const tx = await treasurevaultContract.resetAllPackagePoolAccumulations();
            
            showMessage('Transaction submitted. Waiting for confirmation...', 'info');
            await tx.wait();
            
            showMessage('All package accumulations reset successfully!', 'success');
            
            // Refresh data
            await loadPoolData(readOnlyContract);
            
        } catch (error) {
            console.error('Error resetting all package accumulations:', error);
            showMessage('Failed to reset all package accumulations: ' + (error.reason || error.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Reset royalty accumulation
    const resetRoyaltyAccumulation = async () => {
        if (!treasurevaultContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!window.confirm('Are you sure you want to reset the royalty accumulation?')) {
            return;
        }
        
        try {
            setLoading(true);
            showMessage('Resetting royalty accumulation...', 'info');
            
            const tx = await treasurevaultContract.resetRoyaltyAccumulation();
            
            showMessage('Transaction submitted. Waiting for confirmation...', 'info');
            await tx.wait();
            
            showMessage('Royalty accumulation reset successfully!', 'success');
            
            // Refresh data
            await loadPoolData(readOnlyContract);
            
        } catch (error) {
            console.error('Error resetting royalty accumulation:', error);
            showMessage('Failed to reset royalty accumulation: ' + (error.reason || error.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Claim full reserve pool
    const claimFullReserve = async () => {
        if (!treasurevaultContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!window.confirm(`Are you sure you want to claim the entire reserve pool (${reservePool} USDT)?`)) {
            return;
        }
        
        try {
            setLoading(true);
            showMessage('Claiming full reserve pool...', 'info');
            
            const tx = await treasurevaultContract.claimFullReservePool();
            
            showMessage('Transaction submitted. Waiting for confirmation...', 'info');
            await tx.wait();
            
            showMessage('Successfully claimed full reserve pool!', 'success');
            
            // Refresh data
            await loadContractStatistics(readOnlyContract);
            
        } catch (error) {
            console.error('Error claiming full reserve pool:', error);
            showMessage('Failed to claim full reserve pool: ' + (error.reason || error.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Load eligible users for a pool
    const loadEligibleUsers = async (packageId) => {
        setLoadingEligibleUsers(true);
        setSelectedPoolPackage(packageId);
        setShowEligibleUsersModal(true);
        
        try {
            const users = [];
            const totalUsersNum = parseInt(totalUsers);
            
            for (let i = 1; i <= totalUsersNum && i <= 1000; i++) {
                try {
                    const userDetail = await readOnlyContract.getUserDetails(i);
                    const isEligible = await readOnlyContract.poolEligibility(i, packageId);
                    
                    if (isEligible) {
                        users.push({
                            id: i,
                            address: userDetail.account
                        });
                    }
                } catch (error) {
                    // Skip users that don't exist
                    continue;
                }
            }
            
            setEligibleUsers(users);
        } catch (error) {
            console.error('Error loading eligible users:', error);
            showMessage('Failed to load eligible users', 'error');
        } finally {
            setLoadingEligibleUsers(false);
        }
    };

    // Export eligible users to CSV
    const exportEligibleUsers = (packageId) => {
        if (eligibleUsers.length === 0) {
            showMessage('No eligible users to export', 'warning');
            return;
        }
        
        let csv = 'User ID,Address\n';
        eligibleUsers.forEach(user => {
            csv += `${user.id},${user.address}\n`;
        });
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `package_${packageId}_eligible_users.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        showMessage('Eligible users exported successfully!', 'success');
    };

    // User Lookup Functions
    const lookupByAddress = async () => {
        if (!readOnlyContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!searchAddress || !ethers.utils.isAddress(searchAddress)) {
            showMessage('Please enter a valid wallet address', 'error');
            return;
        }
        
        try {
            setLookupLoading(true);
            setLookupResult(null);
            
            // Get user ID from address using existing addressToId mapping
            const userId = await readOnlyContract.addressToId(searchAddress);
            
            if (userId.toString() === '0') {
                showMessage('This address is not registered in the system', 'warning');
                setLookupResult({
                    found: false,
                    address: searchAddress,
                    message: 'Address not registered'
                });
                return;
            }
            
            // Check if it's the default user ID
            if (userId.toString() === '100000') {
                showMessage('This is Default user ID', 'warning');
                setLookupResult({
                    found: false,
                    isDefaultUser: true,
                    message: 'This is Default user ID'
                });
                return;
            }
            
            // Get user details by ID
            const userDetails = await readOnlyContract.getUserDetails(userId);
            
            // Get additional info
            const packages = await readOnlyContract.getUserPackages(userDetails.id);
            const incomeBreakdown = await readOnlyContract.getUserIncomeBreakdown(userDetails.id);
            
            setLookupResult({
                found: true,
                account: userDetails.account,
                id: userDetails.id.toString(),
                referrer: userDetails.referrer.toString(),
                isActive: userDetails.isActive,
                totalDirectReferrals: userDetails.totalDirectReferrals.toString(),
                claimableAmount: ethers.utils.formatEther(userDetails.claimableAmount),
                totalClaimed: ethers.utils.formatEther(userDetails.totalClaimed),
                totalIncome: ethers.utils.formatEther(userDetails.totalIncome),
                activePackages: packages,
                incomeBreakdown: {
                    directReferralIncome: ethers.utils.formatEther(incomeBreakdown.directReferralIncome),
                    matrixLevelIncome: ethers.utils.formatEther(incomeBreakdown.matrixLevelIncome),
                    normalLevelIncome: ethers.utils.formatEther(incomeBreakdown.normalLevelIncome),
                    poolIncome: ethers.utils.formatEther(incomeBreakdown.poolIncome),
                    royaltyIncome: ethers.utils.formatEther(incomeBreakdown.royaltyIncome)
                }
            });
            
            showMessage('User found successfully!', 'success');
            
        } catch (error) {
            console.error('Error looking up user by address:', error);
            showMessage('Failed to lookup user: ' + (error.reason || error.message), 'error');
        } finally {
            setLookupLoading(false);
        }
    };

    const lookupById = async () => {
        if (!readOnlyContract) {
            showMessage('Please connect your wallet first', 'error');
            return;
        }
        
        if (!searchUserId || parseInt(searchUserId) <= 0) {
            showMessage('Please enter a valid user ID', 'error');
            return;
        }
        
        // Check if it's the default user ID
        if (searchUserId === '100000') {
            showMessage('This is Default user ID', 'warning');
            setLookupResult({
                found: false,
                isDefaultUser: true,
                message: 'This is Default user ID'
            });
            return;
        }
        
        try {
            setLookupLoading(true);
            setLookupResult(null);
            
            // Try to get user details - will return id=0 if user doesn't exist
            const userDetails = await readOnlyContract.getUserDetails(searchUserId);
            
            // Check if user exists (id will be 0 if doesn't exist)
            if (userDetails.id.toString() === '0') {
                showMessage('This user ID does not exist in the system', 'warning');
                setLookupResult({
                    found: false,
                    id: searchUserId,
                    message: 'User ID not found'
                });
                return;
            }
            
            // Get additional info
            const packages = await readOnlyContract.getUserPackages(searchUserId);
            const incomeBreakdown = await readOnlyContract.getUserIncomeBreakdown(searchUserId);
            
            setLookupResult({
                found: true,
                account: userDetails.account,
                id: userDetails.id.toString(),
                referrer: userDetails.referrer.toString(),
                isActive: userDetails.isActive,
                totalDirectReferrals: userDetails.totalDirectReferrals.toString(),
                claimableAmount: ethers.utils.formatEther(userDetails.claimableAmount),
                totalClaimed: ethers.utils.formatEther(userDetails.totalClaimed),
                totalIncome: ethers.utils.formatEther(userDetails.totalIncome),
                activePackages: packages,
                incomeBreakdown: {
                    directReferralIncome: ethers.utils.formatEther(incomeBreakdown.directReferralIncome),
                    matrixLevelIncome: ethers.utils.formatEther(incomeBreakdown.matrixLevelIncome),
                    normalLevelIncome: ethers.utils.formatEther(incomeBreakdown.normalLevelIncome),
                    poolIncome: ethers.utils.formatEther(incomeBreakdown.poolIncome),
                    royaltyIncome: ethers.utils.formatEther(incomeBreakdown.royaltyIncome)
                }
            });
            
            showMessage('User found successfully!', 'success');
            
        } catch (error) {
            console.error('Error looking up user by ID:', error);
            if (error.message.includes('invalid BigNumber') || error.message.includes('out of range')) {
                showMessage('User ID not found in the system', 'warning');
                setLookupResult({
                    found: false,
                    id: searchUserId,
                    message: 'User ID not found'
                });
            } else {
                showMessage('Failed to lookup user: ' + (error.reason || error.message), 'error');
            }
        } finally {
            setLookupLoading(false);
        }
    };

    // Show message helper
    const showMessage = (msg, type = 'info') => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => setMessage(''), 5000);
    };

    // Format address for display
    const formatAddress = (address) => {
        if (!address) return '';
        return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    };

    return (
        <div className="container">
            {/* Message Display */}
            {message && (
                <div 
                    className={`message ${messageType}`}
                    onClick={() => setMessage('')}
                >
                    {message}
                </div>
            )}

            {/* Header */}
            <div className="header">
                <h1 className="title">🏦 Smartcore Admin Panel</h1>
                <div className="wallet-info">
                    {connected ? (
                        <>
                            <span className="connected">✓ Connected</span>
                            <span className="wallet-address">{formatAddress(currentAccount)}</span>
                            <button className="connect-btn" onClick={disconnectWallet}>
                                Disconnect
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="disconnected">✗ Not Connected</span>
                            <button className="connect-btn" onClick={connectWallet} disabled={loading}>
                                {loading ? 'Connecting...' : 'Connect Wallet'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Main Content */}
            {connected && !isAuthorized && (
                <div className="admin-content">
                    <section className="section">
                        <div style={{textAlign: 'center', padding: '50px 20px'}}>
                            <h2 style={{color: '#e53e3e', fontSize: '2rem', marginBottom: '20px'}}>
                                🔒 Access Denied
                            </h2>
                            <p style={{fontSize: '1.2rem', marginBottom: '30px', color: '#4a5568'}}>
                                This admin panel is restricted to authorized users only.
                            </p>
                            <div style={{background: '#fff5f5', border: '2px solid #fc8181', borderRadius: '12px', padding: '25px', maxWidth: '600px', margin: '0 auto'}}>
                                <h3 style={{color: '#c53030', marginBottom: '15px'}}>Authorization Required</h3>
                                <p style={{marginBottom: '10px', color: '#2d3748'}}>
                                    <strong>Your Address:</strong><br/>
                                    <code style={{background: '#fff', padding: '5px 10px', borderRadius: '5px', fontSize: '0.9rem'}}>
                                        {currentAccount}
                                    </code>
                                </p>
                                
                                <div style={{marginTop: '25px', padding: '15px', background: '#fffaf0', borderRadius: '8px', borderLeft: '4px solid #ed8936'}}>
                                    <p style={{color: '#7c2d12', margin: 0}}>
                                        <strong>💡 Note:</strong> Please connect with the admin wallet to access this panel.
                                    </p>
                                </div>
                            </div>
                            <button 
                                className="connect-btn"
                                onClick={disconnectWallet}
                                style={{marginTop: '30px'}}
                            >
                                Disconnect Wallet
                            </button>
                        </div>
                    </section>
                </div>
            )}

            {/* Main Content - Only for Authorized Users */}
            {connected && isAuthorized && (
                <div className="admin-content">
                    {/* Statistics Section */}
                    <section className="section">
                        <h2 className="section-title">📊 Contract Statistics</h2>
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3>Total Users</h3>
                                <div className="stat-value">{totalUsers}</div>
                                <small>Registered in the system</small>
                            </div>
                            <div className="stat-card" style={{background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'}}>
                                <h3>Last User ID</h3>
                                <div className="stat-value">{lastUserId}</div>
                                <small>Most recent registration</small>
                            </div>
                            <div className="stat-card" style={{background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)'}}>
                                <h3>Reserve Pool</h3>
                                <div className="stat-value">{parseFloat(reservePool).toFixed(2)} USDT</div>
                                <small>Available for claim</small>
                            </div>
                        </div>
                    </section>

                    {/* Pool Accumulation Breakdown */}
                    <section className="section">
                        <h2 className="section-title">💰 Pool Accumulation Breakdown</h2>
                        <div className="stats-grid">
                            <div className="stat-card" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                                <h3>Total Pool Funds</h3>
                                <div className="stat-value">{parseFloat(poolFundsAccumulated).toFixed(2)} USDT</div>
                                <small>Accumulated across all packages</small>
                                <button 
                                    className="action-btn"
                                    onClick={resetPoolAccumulation}
                                    disabled={loading}
                                    style={{marginTop: '15px', fontSize: '0.85rem', padding: '8px 16px'}}
                                >
                                    Reset Total
                                </button>
                            </div>
                            <div className="stat-card" style={{background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'}}>
                                <h3>Total Royalty Funds</h3>
                                <div className="stat-value">{parseFloat(royaltyFundsAccumulated).toFixed(2)} USDT</div>
                                <small>Accumulated for royalty pool</small>
                                <button 
                                    className="action-btn"
                                    onClick={resetRoyaltyAccumulation}
                                    disabled={loading}
                                    style={{marginTop: '15px', fontSize: '0.85rem', padding: '8px 16px'}}
                                >
                                    Reset Royalty
                                </button>
                            </div>
                        </div>
                        
                        {/* Per-Package Accumulation */}
                        <h3 style={{marginTop: '25px', marginBottom: '15px', color: '#2d3748'}}>Per-Package Accumulation</h3>
                        <div className="pools-grid">
                            {[2, 3, 4, 5, 6, 7, 8].map(packageId => (
                                <div key={packageId} className="pool-card">
                                    <h4>Package {packageId}</h4>
                                    <div className="pool-balance">
                                        {parseFloat(packagePoolAccumulations[packageId] || '0').toFixed(2)} USDT
                                    </div>
                                    <div className="pool-info">
                                        <span>Eligible: {poolEligibleCounts[packageId] || 0}</span>
                                    </div>
                                    <button 
                                        className="pool-btn"
                                        onClick={() => resetPackageAccumulation(packageId)}
                                        disabled={loading}
                                        style={{fontSize: '0.8rem', padding: '6px 12px'}}
                                    >
                                        Reset
                                    </button>
                                </div>
                            ))}
                        </div>
                        
                        <button 
                            className="action-btn"
                            onClick={resetAllPackageAccumulations}
                            disabled={loading}
                            style={{marginTop: '20px', width: '100%'}}
                        >
                            Reset All Package Accumulations
                        </button>
                    </section>

                    {/* Pool Distributions */}
                    <section className="section">
                        <h2 className="section-title">🎯 Pool Distribution Management</h2>
                        
                        {/* Package Pool Distribution */}
                        <div className="virtual-card">
                            <h3>Set Package Pool Income</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Package ID (2-8):</label>
                                    <input 
                                        type="number"
                                        min="2"
                                        max="8"
                                        className="input-field"
                                        value={poolDistForm.packageId}
                                        onChange={(e) => setPoolDistForm({...poolDistForm, packageId: e.target.value})}
                                        placeholder="2"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Amount per User (USDT):</label>
                                    <input 
                                        type="number"
                                        step="0.01"
                                        className="input-field"
                                        value={poolDistForm.amount}
                                        onChange={(e) => setPoolDistForm({...poolDistForm, amount: e.target.value})}
                                        placeholder="10.00"
                                    />
                                </div>
                            </div>
                            
                            {poolDistForm.packageId && poolDistForm.amount && (
                                <div className="calculation-box">
                                    <p><strong>Eligible Users:</strong> {poolCalculation.eligibleCount}</p>
                                    <p><strong>Total Required:</strong> {poolCalculation.totalRequired} USDT</p>
                                </div>
                            )}
                            
                            <button 
                                className="action-btn"
                                onClick={setPoolIncome}
                                disabled={loading}
                            >
                                Set Pool Income
                            </button>
                        </div>

                        {/* Royalty Pool Distribution */}
                        <div className="virtual-card" style={{marginTop: '20px'}}>
                            <h3>Set Royalty Pool Income</h3>
                            <div className="form-group">
                                <label>Amount per User (USDT):</label>
                                <input 
                                    type="number"
                                    step="0.01"
                                    className="input-field"
                                    value={royaltyDistForm.amount}
                                    onChange={(e) => setRoyaltyDistForm({...royaltyDistForm, amount: e.target.value})}
                                    placeholder="50.00"
                                />
                            </div>
                            
                            {royaltyDistForm.amount && (
                                <div className="calculation-box">
                                    <p><strong>Eligible Users:</strong> {royaltyCalculation.eligibleCount}</p>
                                    <p><strong>Total Required:</strong> {royaltyCalculation.totalRequired} USDT</p>
                                </div>
                            )}
                            
                            <button 
                                className="action-btn"
                                onClick={setRoyaltyIncome}
                                disabled={loading}
                            >
                                Set Royalty Income
                            </button>
                        </div>

                        {/* Current Distribution Status */}
                        <div className="virtual-card" style={{marginTop: '20px'}}>
                            <h3>Current Distribution Status</h3>
                            <div className="distribution-grid">
                                {[2, 3, 4, 5, 6, 7, 8].map(packageId => (
                                    <div key={packageId} className="distribution-item">
                                        <h4>Package {packageId}</h4>
                                        <p>Round: {poolDistributions[packageId]?.currentRound || '0'}</p>
                                        <p>Latest: {parseFloat(poolDistributions[packageId]?.latestAmount || '0').toFixed(2)} USDT</p>
                                        <p>Eligible: {poolEligibleCounts[packageId] || '0'}</p>
                                        <button 
                                            className="pool-btn"
                                            onClick={() => loadEligibleUsers(packageId)}
                                            disabled={loading}
                                        >
                                            View Users
                                        </button>
                                    </div>
                                ))}
                                <div className="distribution-item" style={{background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'}}>
                                    <h4>Royalty Pool</h4>
                                    <p>Round: {royaltyDistribution.currentRound}</p>
                                    <p>Latest: {parseFloat(royaltyDistribution.latestAmount).toFixed(2)} USDT</p>
                                    <p>Eligible: {royaltyEligibleCount}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* User Lookup Section */}
                    <section className="section">
                        <h2 className="section-title">🔍 User Lookup</h2>
                        <div className="virtual-grid">
                            {/* Search by Address */}
                            <div className="virtual-card">
                                <h3>Search by Wallet Address</h3>
                                <div className="form-group">
                                    <label>Wallet Address:</label>
                                    <input 
                                        type="text"
                                        className="input-field"
                                        value={searchAddress}
                                        onChange={(e) => setSearchAddress(e.target.value)}
                                        placeholder="0x..."
                                    />
                                </div>
                                <button 
                                    className="action-btn"
                                    onClick={lookupByAddress}
                                    disabled={lookupLoading}
                                >
                                    {lookupLoading ? 'Searching...' : 'Search by Address'}
                                </button>
                            </div>

                            {/* Search by ID */}
                            <div className="virtual-card">
                                <h3>Search by User ID</h3>
                                <div className="form-group">
                                    <label>User ID:</label>
                                    <input 
                                        type="number"
                                        className="input-field"
                                        value={searchUserId}
                                        onChange={(e) => setSearchUserId(e.target.value)}
                                        placeholder="123456"
                                    />
                                </div>
                                <button 
                                    className="action-btn"
                                    onClick={lookupById}
                                    disabled={lookupLoading}
                                >
                                    {lookupLoading ? 'Searching...' : 'Search by ID'}
                                </button>
                            </div>
                        </div>

                        {/* Lookup Results */}
                        {lookupResult && (
                            <div className="virtual-card" style={{marginTop: '20px'}}>
                                {lookupResult.found ? (
                                    <>
                                        <h3 style={{color: '#48bb78', marginBottom: '20px'}}>✅ User Found</h3>
                                        <div style={{background: '#f7fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px'}}>
                                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px'}}>
                                                <div>
                                                    <strong>User ID:</strong>
                                                    <p style={{margin: '5px 0', fontSize: '1.1rem', color: '#2d3748'}}>{lookupResult.id}</p>
                                                </div>
                                                <div>
                                                    <strong>Wallet Address:</strong>
                                                    <p 
                                                        style={{margin: '5px 0', fontSize: '0.9rem', color: '#4299e1', cursor: 'pointer', wordBreak: 'break-all'}}
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(lookupResult.account);
                                                            showMessage('Address copied!', 'success');
                                                        }}
                                                        title="Click to copy"
                                                    >
                                                        {lookupResult.account}
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Referrer ID:</strong>
                                                    <p style={{margin: '5px 0', fontSize: '1.1rem', color: '#2d3748'}}>{lookupResult.referrer}</p>
                                                </div>
                                                <div>
                                                    <strong>Status:</strong>
                                                    <p style={{margin: '5px 0', fontSize: '1.1rem', color: lookupResult.isActive ? '#48bb78' : '#f56565'}}>
                                                        {lookupResult.isActive ? '✅ Active' : '❌ Inactive'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Direct Referrals:</strong>
                                                    <p style={{margin: '5px 0', fontSize: '1.1rem', color: '#2d3748'}}>{lookupResult.totalDirectReferrals}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{background: '#fff5f5', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '2px solid #fed7d7'}}>
                                            <h4 style={{marginBottom: '15px', color: '#2d3748'}}>💰 Income Summary</h4>
                                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
                                                <div>
                                                    <strong>Total Income:</strong>
                                                    <p style={{margin: '5px 0', fontSize: '1.2rem', color: '#48bb78', fontWeight: 'bold'}}>
                                                        {parseFloat(lookupResult.totalIncome).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Claimable:</strong>
                                                    <p style={{margin: '5px 0', fontSize: '1.2rem', color: '#ed8936', fontWeight: 'bold'}}>
                                                        {parseFloat(lookupResult.claimableAmount).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Total Claimed:</strong>
                                                    <p style={{margin: '5px 0', fontSize: '1.2rem', color: '#667eea', fontWeight: 'bold'}}>
                                                        {parseFloat(lookupResult.totalClaimed).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{background: '#fffff0', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '2px solid #faf089'}}>
                                            <h4 style={{marginBottom: '15px', color: '#2d3748'}}>📊 Income Breakdown</h4>
                                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px'}}>
                                                <div>
                                                    <strong>Direct Referral:</strong>
                                                    <p style={{margin: '5px 0', color: '#2d3748'}}>
                                                        {parseFloat(lookupResult.incomeBreakdown.directReferralIncome).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Matrix Level:</strong>
                                                    <p style={{margin: '5px 0', color: '#2d3748'}}>
                                                        {parseFloat(lookupResult.incomeBreakdown.matrixLevelIncome).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Normal Level:</strong>
                                                    <p style={{margin: '5px 0', color: '#2d3748'}}>
                                                        {parseFloat(lookupResult.incomeBreakdown.normalLevelIncome).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Pool Income:</strong>
                                                    <p style={{margin: '5px 0', color: '#2d3748'}}>
                                                        {parseFloat(lookupResult.incomeBreakdown.poolIncome).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                                <div>
                                                    <strong>Royalty Income:</strong>
                                                    <p style={{margin: '5px 0', color: '#2d3748'}}>
                                                        {parseFloat(lookupResult.incomeBreakdown.royaltyIncome).toFixed(4)} USDT
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{background: '#e6fffa', padding: '20px', borderRadius: '8px', border: '2px solid #81e6d9'}}>
                                            <h4 style={{marginBottom: '15px', color: '#2d3748'}}>📦 Active Packages</h4>
                                            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                                                {lookupResult.activePackages.map((isActive, index) => (
                                                    <div 
                                                        key={index}
                                                        style={{
                                                            padding: '8px 16px',
                                                            borderRadius: '20px',
                                                            background: isActive ? '#48bb78' : '#cbd5e0',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        Package {index + 1}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <h3 style={{color: '#f56565', marginBottom: '15px'}}>
                                            {lookupResult.isDefaultUser ? '⚠️ Warning' : '❌ Not Found'}
                                        </h3>
                                        <p style={{color: '#718096', fontSize: '1.1rem'}}>
                                            {lookupResult.message}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </section>

                    {/* Reserve Pool */}
                    <section className="section">
                        <h2 className="section-title">💎 Reserve Pool Management</h2>
                        <div className="reserve-card">
                            <div className="reserve-info">
                                <h3>Reserve Pool Balance</h3>
                                <div className="reserve-balance">{reservePool} USDT</div>
                                <small>Funds from ceiling limits and system operations</small>
                            </div>
                            
                            <div style={{marginTop: '25px'}}>
                                <button 
                                    className="claim-btn"
                                    onClick={claimFullReserve}
                                    disabled={loading}
                                    style={{width: '100%', backgroundColor: 'rgba(229, 62, 62, 0.3)', border: '2px solid #e53e3e'}}
                                >
                                    Claim Reserve Pool
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* System Status */}
                    <section className="section">
                        <h2 className="section-title">⚙️ System Status</h2>
                        <div className="virtual-card">
                            <h3>Contract Information</h3>
                            <p><strong>Network:</strong> OPBNB (Chain ID: {CONFIG.CHAIN_ID})</p>
                            <p><strong>Contract:</strong> {CONFIG.TREASUREVAULT_CONTRACT}</p>
                            <p><strong>USDT Contract:</strong> {CONFIG.USDT_CONTRACT}</p>
                            <p><strong>Current User ID:</strong> {currentUserId || 'Not registered'}</p>
                            <p><strong>Total Users:</strong> {totalUsers}</p>
                            <p><strong>Total Pool Funds:</strong> {poolFundsAccumulated} USDT</p>
                            <p><strong>Total Royalty Funds:</strong> {royaltyFundsAccumulated} USDT</p>
                            <button 
                                className="action-btn"
                                onClick={async () => {
                                    if (readOnlyContract) {
                                        await Promise.all([
                                            loadContractStatistics(readOnlyContract),
                                            loadPoolDistributions(readOnlyContract),
                                            loadRoyaltyDistribution(readOnlyContract),
                                            loadPoolData(readOnlyContract)
                                        ]);
                                    }
                                    if (currentAccount && usdtContract) {
                                        await Promise.all([
                                            loadUserData(currentAccount, readOnlyContract),
                                            loadUSDTBalance(currentAccount, usdtContract)
                                        ]);
                                    }
                                }}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Refresh All Data'}
                            </button>
                        </div>
                    </section>
                </div>
            )}

            {/* Eligible Users Modal */}
            {showEligibleUsersModal && (
                <div className="modal" onClick={() => setShowEligibleUsersModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Package {selectedPoolPackage} - Eligible Users</h2>
                            <button 
                                className="close-btn"
                                onClick={() => setShowEligibleUsersModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            {loadingEligibleUsers ? (
                                <div style={{textAlign: 'center', padding: '20px'}}>
                                    <div className="spinner"></div>
                                    <p>Loading eligible users...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="total-count">
                                        Total Eligible Users: {eligibleUsers.length}
                                    </div>
                                    {eligibleUsers.length > 0 && (
                                        <button 
                                            className="export-btn"
                                            onClick={() => exportEligibleUsers(selectedPoolPackage)}
                                            style={{marginBottom: '15px'}}
                                        >
                                            Export to CSV
                                        </button>
                                    )}
                                    <div className="users-list">
                                        {eligibleUsers.map(user => (
                                            <div key={user.id} className="user-item">
                                                <strong>User ID:</strong> {user.id}
                                                <span 
                                                    className="user-address"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(user.address);
                                                        showMessage('Address copied!', 'success');
                                                    }}
                                                    title="Click to copy"
                                                >
                                                    {user.address}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Processing...</p>
                </div>
            )}
        </div>
    );
};

export default SmartcoreAdminPanel;